import { useTranslation } from 'react-i18next'
import type { SearchState } from '@/api/types'

interface Props {
  value: SearchState
  onChange: (next: SearchState) => void
  maxOccupancy?: number
  layout?: 'stacked' | 'grid'
}

export function BookingDateGuestPicker({
  value,
  onChange,
  maxOccupancy,
  layout = 'stacked',
}: Props) {
  const { t } = useTranslation()
  const inputCls =
    'h-10 w-full rounded-md border border-cream-dark bg-white px-3 text-sm text-wood-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-1'
  const labelCls = 'text-[11px] font-semibold uppercase tracking-wider text-stone'

  const totalGuests = value.adults + value.children
  const overCapacity = maxOccupancy != null && totalGuests > maxOccupancy

  const set = (patch: Partial<SearchState>) => onChange({ ...value, ...patch })

  const containerCls =
    layout === 'grid'
      ? 'grid grid-cols-2 gap-3'
      : 'grid grid-cols-1 gap-3 sm:grid-cols-2'

  return (
    <div className="space-y-2">
      <div className={containerCls}>
        <label className="flex flex-col gap-1">
          <span className={labelCls}>
            {t('search.check_in', { defaultValue: 'Llegada' })}
          </span>
          <input
            type="date"
            value={value.check_in}
            onChange={(e) => set({ check_in: e.target.value })}
            className={inputCls}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className={labelCls}>
            {t('search.check_out', { defaultValue: 'Salida' })}
          </span>
          <input
            type="date"
            value={value.check_out}
            min={value.check_in}
            onChange={(e) => set({ check_out: e.target.value })}
            className={inputCls}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className={labelCls}>
            {t('search.adults', { defaultValue: 'Adultos' })}
          </span>
          <input
            type="number"
            min={1}
            max={maxOccupancy}
            value={value.adults}
            onChange={(e) => set({ adults: Math.max(1, Number(e.target.value) || 1) })}
            className={inputCls}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className={labelCls}>
            {t('search.children', { defaultValue: 'Niños' })}
          </span>
          <input
            type="number"
            min={0}
            max={maxOccupancy}
            value={value.children}
            onChange={(e) => set({ children: Math.max(0, Number(e.target.value) || 0) })}
            className={inputCls}
          />
        </label>
      </div>
      {overCapacity && (
        <p className="text-xs text-terracotta">
          La capacidad máxima de esta habitación es {maxOccupancy} huéspedes.
        </p>
      )}
    </div>
  )
}
